<template>
    <div class="page-container">
        <div class="page-header content-fade-in">
            <h1 class="title">上传录音</h1>
            <div class="subtitle">选择本地音频文件上传，AI即时转写并进行总结</div>
        </div>
        
        <div class="content-container content-slide-up" style="animation-delay: 0.1s;">
            <div class="form-header">
                <div class="upload-icon-box">
                    <i class="upload-icon-inner"></i>
                </div>
                <div class="upload-desc">上传本地音频文件，开始智能识别转写</div>
            </div>
            
            <div class="form-wrapper">
                <a-form ref="formRef" class="mt-form" :model="formState" name="dynamic_rule" v-bind="formItemLayout" hideRequiredMark>
                    <a-form-item label="会议名称" name="name" :rules="[{ required: true, message: '请输入会议名称' }]">
                        <a-input v-model:value="formState.name" placeholder="请输入会议名称" allowClear />
                    </a-form-item>

                    <a-form-item label="参会人员" name="users">
                        <a-select v-model:value="formState.users" mode="multiple" label-in-value placeholder="选择参会人员"
                            style="width: 100%" :filter-option="false" :not-found-content="state.fetching ? undefined : null"
                            :options="state.data">
                            <template v-if="state.fetching" #notFoundContent>
                                <a-spin size="small" />
                            </template>
                        </a-select>
                    </a-form-item>

                    <a-form-item label="上传录音" name="file" :rules="[{ required: true, message: '请选择录音文件' }]">
                        <div class="upload-area" :class="{'upload-area-active': formState.file}">
                            <template v-if="!formState.file">
                                <div class="upload-placeholder" @click="triggerUpload">
                                    <upload-outlined class="upload-icon" />
                                    <p>点击或拖拽文件到此处上传</p>
                                    <p class="upload-hint">支持WAV、MP3、M4A格式</p>
                                </div>
                            </template>
                            <template v-else>
                                <div class="file-item card-item">
                                    <file-outlined class="file-icon" />
                                    <div class="file-info">
                                        <div class="file-name">{{ formState.file.name }}</div>
                                        <div class="file-size">{{ formatFileSize(formState.file.size) }}</div>
                                    </div>
                                    <delete-outlined class="delete-icon" @click="removeFile" />
                                </div>
                            </template>
                            <input 
                                type="file" 
                                ref="fileInput" 
                                style="display: none" 
                                accept=".wav,.mp3,.m4a" 
                                @change="handleFileChange"
                            />
                        </div>
                    </a-form-item>
                    
                    <div class="form-button-container">
                        <a-button type="primary" size="large" class="upload-button button-shimmer" @click="upload" :loading="uploading">
                            {{ uploading ? '上传中...' : '立即识别' }}
                        </a-button>
                    </div>
                </a-form>
            </div>
            
            <div class="upload-tips">
                <div class="tips-title">上传小贴士</div>
                <ul class="tips-list">
                    <li>支持上传的音频格式包括WAV和MP3</li>
                    <li>请确保音频质量清晰，背景噪音较小</li>
                    <li>建议音频长度不超过3小时，以获得更好的处理效果</li>
                    <li>添加参会人员信息有助于AI更准确地识别发言人</li>
                    <li>上传后系统将自动进行语音识别和智能分析</li>
                </ul>
                
                <div class="file-format-info">
                    <div class="format-header">文件要求</div>
                    <div class="format-items">
                        <div class="format-item">
                            <span class="format-label">格式：</span>
                            <span class="format-value">WAV, MP3, M4A</span>
                        </div>
                        <div class="format-item">
                            <span class="format-label">最大大小：</span>
                            <span class="format-value">500MB</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, watch, computed, onActivated } from 'vue';
import { message } from 'ant-design-vue';
import { UploadOutlined, FileOutlined, DeleteOutlined } from '@ant-design/icons-vue';
import { useRouter } from 'vue-router';
import { BASE_URL } from '@/config/api';

const router = useRouter();
const formRef = ref();
const fileInput = ref(null);
const uploading = ref(false);

const formState = reactive({
    name: '',
    users: [],
    file: null,
});

const myPermission = computed(() => {
    const permission = localStorage.getItem('myPermission') || '{}';
    return JSON.parse(permission);
});

const state = reactive({
    data: [],
    value: [],
    fetching: false,
});

let lastFetchId = 0;

// 重置表单数据的函数
const resetForm = () => {
    formState.name = '';
    formState.users = [];
    formState.file = null;
};

// 在组件被激活时调用（每次进入页面时）
onActivated(() => {
    resetForm();
});

// 初始化用户数据
const handleChange = () => {
    lastFetchId += 1;
    const fetchId = lastFetchId;
    state.data = [];
    state.fetching = true;
    fetch(`${BASE_URL}/user?active_id=${myPermission.value.id}`)
        .then(response => response.json())
        .then(body => {
            if (fetchId !== lastFetchId) {
                // for fetch callback order
                return;
            }
            const data = body.items.map(user => ({
                label: user.name,
                value: user.id,
            }));
            state.data = data;
            state.fetching = false;
        }).catch(() => { 
            state.fetching = false;
        });
};

handleChange();

// 格式化文件大小
const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// 触发文件上传
const triggerUpload = () => {
    fileInput.value.click();
};

// 处理文件选择
const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
        const fileExtension = file.name.split('.').pop().toLowerCase();
        if (['wav', 'mp3', 'm4a'].includes(fileExtension)) {
            formState.file = file;
        } else {
            message.error('只支持WAV和MP3格式的音频文件');
        }
    }
    // 重置input，允许选择相同文件
    e.target.value = '';
};

// 移除文件
const removeFile = () => {
    formState.file = null;
};

// 上传文件
const upload = () => {
    formRef.value.validate().then(async () => {
        if (!formState.file) {
            message.error('请选择录音文件');
            return;
        }
        
        uploading.value = true;
        const formData = new FormData();
        formData.append('file', formState.file);
        formData.append('title', formState.name);
        formData.append('usersList', formState.users.map(user => user.key).join(','));
        formData.append('active_id', myPermission.value.id);
        
        try {
            const response = await fetch(`${BASE_URL}/audio`, {
                method: 'POST',
                body: formData,
            });
            
            const data = await response.json();
            message.success('上传成功');
            router.push({ path: '/meetingedit/' + data.id + '?tab=原始记录' });
        } catch (error) {
            console.error('上传失败:', error);
            message.error('上传失败，请稍后重试');
        } finally {
            uploading.value = false;
        }
    });
};

const formItemLayout = {
    wrapperCol: {
        flex: 'auto'
    },
};

watch(state.value, () => {
    state.data = [];
    state.fetching = false;
});

</script>

<style scoped lang="less">
.page-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 24px;
}

.content-container {
    display: grid;
    grid-template-columns: 1fr 300px;
    grid-gap: 24px;
    padding: 0;
    box-shadow: none;
    background: transparent;
    height: auto;
}

.form-header {
    display: flex;
    align-items: center;
    margin-bottom: 24px;
    padding: 0 24px;
}

.upload-icon-box {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, #3B82F6, #246BF0);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 16px;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
}

.upload-icon-inner {
    width: 24px;
    height: 24px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12' /%3E%3C/svg%3E");
    background-size: contain;
}

.upload-desc {
    font-size: 16px;
    font-weight: 500;
    color: #1E293B;
}

.form-wrapper {
    background-color: #fff;
    border-radius: 12px;
    box-shadow: var(--card-shadow);
    padding: 32px 40px;
    grid-column: 1 / 2;
}

.upload-tips {
    background-color: #fff;
    border-radius: 12px;
    padding: 32px 40px;
    box-shadow: var(--card-shadow);
    grid-column: 2 / 3;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.tips-title {
    font-size: 16px;
    font-weight: 600;
    color: #0F172A;
    margin-bottom: 16px;
    border-bottom: 1px solid #E2E8F0;
    padding-bottom: 12px;
}

.tips-list {
    padding-left: 20px;
    margin: 0 0 24px 0;
    
    li {
        margin-bottom: 12px;
        color: #64748B;
        line-height: 1.5;
        
        &:last-child {
            margin-bottom: 0;
        }
    }
}

.file-format-info {
    background-color: #F8FAFC;
    border-radius: 8px;
    padding: 16px;
    margin-top: 20px;
}

.format-header {
    font-weight: 600;
    color: #334155;
    margin-bottom: 12px;
    font-size: 14px;
}

.format-items {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.format-item {
    display: flex;
    font-size: 13px;
    
    .format-label {
        color: #64748B;
        min-width: 80px;
    }
    
    .format-value {
        color: #334155;
        font-weight: 500;
    }
}

.mt-form {
    width: 100%;
}

.upload-area {
    background-color: #F8FAFC;
    border: 2px dashed #CBD5E1;
    border-radius: 12px;
    padding: 20px;
    transition: all 0.3s ease;
    text-align: center;
    
    &:hover {
        border-color: #3B82F6;
        background-color: #EFF6FF;
    }
    
    &.upload-area-active {
        border-color: #3B82F6;
        background-color: #EFF6FF;
    }
}

.upload-placeholder {
    cursor: pointer;
    padding: 30px 20px;
    
    .upload-icon {
        font-size: 36px;
        color: #64748B;
        margin-bottom: 12px;
    }
    
    p {
        color: #64748B;
        margin: 8px 0;
    }
    
    .upload-hint {
        font-size: 12px;
        color: #94A3B8;
    }
}

.file-item {
    display: flex;
    align-items: center;
    padding: 16px;
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    
    .file-icon {
        font-size: 24px;
        color: #3B82F6;
        margin-right: 16px;
    }
    
    .file-info {
        flex: 1;
        text-align: left;
        
        .file-name {
            font-weight: 500;
            margin-bottom: 4px;
            color: #1E293B;
        }
        
        .file-size {
            font-size: 12px;
            color: #64748B;
        }
    }
    
    .delete-icon {
        font-size: 18px;
        color: #94A3B8;
        cursor: pointer;
        padding: 8px;
        transition: all 0.2s ease;
        
        &:hover {
            color: #EF4444;
        }
    }
}

.form-button-container {
    display: flex;
    justify-content: center;
    margin-top: 40px;
}

.upload-button {
    min-width: 200px;
    height: 48px !important;
    font-size: 16px;
    font-weight: 600;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    background: linear-gradient(135deg, #3B82F6, #246BF0) !important;
    border-color: #3B82F6 !important;
}

@media (max-width: 1200px) {
    .content-container {
        grid-template-columns: 1fr;
    }
    
    .upload-tips {
        grid-column: 1 / 2;
    }
}
</style>